// src/components/themes/default/admin/BranchCustomersTab.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  Users,
  Search,
  Eye,
  Trash2,
  X,
  Clock,
  Mail,
  Phone,
  RefreshCw,
  UserPlus,
  Loader2,
  Check,
  Store,
  Calendar,
} from "lucide-react";

interface BranchCustomersTabProps {
  subdomain: string;
  branchId: number;
  branchName: string;
  primaryColor?: string;
  theme?: "dark" | "light";
}

export default function BranchCustomersTab({
  subdomain,
  branchId,
  branchName,
  primaryColor = "#059669",
  theme = "dark",
}: BranchCustomersTabProps) {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [viewCustomer, setViewCustomer] = useState<any | null>(null);

  // কাস্টমার তৈরির মোডাল স্টেট
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const getApiUrl = (endpoint: string) => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") ||
        window.location.hostname === "127.0.0.1");
    return isLocal
      ? `http://${subdomain}.localhost:8000${endpoint}`
      : endpoint;
  };

  const getAuthToken = () => {
    if (typeof window === "undefined") return "";
    const pathParts = window.location.pathname.split("/b/");
    const currentBranchSlug = pathParts[1]?.split("/")[0]?.split("?")[0] || "";

    if (currentBranchSlug) {
      const branchAuth = localStorage.getItem(`branch_auth_${subdomain}_${currentBranchSlug}`);
      if (branchAuth) {
        try {
          const parsed = JSON.parse(branchAuth);
          if (parsed.token) return parsed.token;
        } catch {}
      }
    }
    return localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";
  };

  // 🌟 ১. শুধুমাত্র এই নির্দিষ্ট ব্রাঞ্চের কাস্টমার ফেচ করা
  const fetchBranchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl(`/api/v1/customers/?branch_id=${branchId}`), {
        headers: {
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setCustomers(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Error fetching branch customers:", err);
    } finally {
      setLoading(false);
    }
  }, [subdomain, branchId]);

  useEffect(() => {
    fetchBranchCustomers();
  }, [fetchBranchCustomers]);

  // 🌟 ২. ব্রাঞ্চ কাস্টমার যুক্ত করা
  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      Swal.fire({
        position: "top",
        icon: "warning",
        title: "তথ্য দিন",
        text: "কাস্টমারের নাম এবং ইমেইল অ্যাড্রেস আবশ্যক।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
      return;
    }

    setAddLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl(`/api/v1/customers/`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          branch_id: branchId,
        }),
      });

      if (res.ok) {
        setIsAddOpen(false);
        setForm({ name: "", email: "", phone: "" });
        Swal.fire({
          position: "top",
          icon: "success",
          title: "কাস্টমার যুক্ত হয়েছে!",
          text: `"${form.name}" সফলভাবে এই আউটলেটে যুক্ত হয়েছে।`,
          showConfirmButton: false,
          timer: 2000,
          background: "#090d16",
          color: "#ffffff",
          customClass: { container: "!z-[999999]" },
        });
        fetchBranchCustomers();
      } else {
        const data = await res.json().catch(() => ({}));
        Swal.fire({
          position: "top",
          icon: "error",
          title: "ব্যর্থ হয়েছে",
          text: data.error || data.detail || "কাস্টমার যুক্ত করা যায়নি।",
          background: "#090d16",
          color: "#ffffff",
          customClass: { container: "!z-[999999]" },
        });
      }
    } catch {
      Swal.fire({
        position: "top",
        icon: "error",
        title: "সার্ভার এরর",
        text: "সার্ভারে যোগাযোগ করা যায়নি।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
    } finally {
      setAddLoading(false);
    }
  };

  // 🌟 ৩. ব্রাঞ্চ কাস্টমার ডিলিট করা
  const handleDeleteCustomer = async (id: number, name: string) => {
    const result = await Swal.fire({
      title: "আপনি কি নিশ্চিত?",
      text: `"${name}" কাস্টমার অ্যাকাউন্টটি ডিলিট করতে চান?`,
      icon: "warning",
      position: "top",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#334155",
      confirmButtonText: "হ্যাঁ, ডিলিট করুন!",
      cancelButtonText: "বাতিল",
      background: "#090d16",
      color: "#ffffff",
      target: "body",
      customClass: {
        container: "!z-[999999]",
        popup: "rounded-3xl border border-slate-800 shadow-2xl mt-6 !z-[1000000]",
      },
    });

    if (!result.isConfirmed) return;

    setDeleteId(id);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl(`/api/v1/customers/${id}/?branch_id=${branchId}`), {
        method: "DELETE",
        headers: {
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        Swal.fire({
          position: "top",
          icon: "success",
          title: "ডিলিট সম্পন্ন!",
          text: `কাস্টমার মুছে ফেলা হয়েছে।`,
          showConfirmButton: false,
          timer: 2000,
          background: "#090d16",
          color: "#ffffff",
          customClass: { container: "!z-[999999]" },
        });
        fetchBranchCustomers();
      } else {
        Swal.fire({
          position: "top",
          icon: "error",
          title: "ব্যর্থ হয়েছে",
          text: "কাস্টমার ডিলিট করা যায়নি।",
          background: "#090d16",
          color: "#ffffff",
          customClass: { container: "!z-[999999]" },
        });
      }
    } catch {
      Swal.fire({
        position: "top",
        icon: "error",
        title: "সার্ভার এরর",
        text: "যোগাযোগ ব্যাহত হয়েছে।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    const name = String(c.full_name || `${c.first_name || ""} ${c.last_name || ""}`).toLowerCase();
    const email = String(c.email || "").toLowerCase();
    const phone = String(c.phone || "");
    return name.includes(q) || email.includes(q) || phone.includes(q);
  });

  return (
    <div className="space-y-5 animate-fadeIn">
      {/* ১. হেডার ও একশন বার */}
      <div className="p-5 sm:p-6 rounded-3xl bg-slate-900 border border-slate-800 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <Users className="w-5 h-5" />
            </div>
            <h1 className="text-lg sm:text-xl font-black">
              {branchName} Customers Directory
            </h1>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            শুধুমাত্র এই আউটলেটের নিবন্ধিত কাস্টমারদের তালিকা ({customers.length} জন কাস্টমার)
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsAddOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black text-white bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-600/30 transition-all cursor-pointer active:scale-95"
          >
            <UserPlus className="w-4 h-4" />
            <span>+ Add Customer</span>
          </button>

          <button
            onClick={fetchBranchCustomers}
            className="p-2.5 rounded-2xl bg-slate-950 border border-slate-800 hover:bg-slate-800 text-slate-400 hover:text-white transition-colors cursor-pointer"
            title="রিফ্রেশ"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-emerald-400" : ""}`} />
          </button>
        </div>
      </div>

      {/* ২. সার্চ বার */}
      <div className="p-3 sm:p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between gap-3 shadow-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or phone number..."
            className="w-full pl-10 pr-4 py-2 rounded-xl text-xs font-bold bg-slate-950 border border-slate-800 text-white outline-none focus:border-emerald-500 transition-all placeholder:text-slate-500"
          />
        </div>
      </div>

      {/* ৩. কাস্টমার টেবিল */}
      <div className="rounded-3xl overflow-hidden border border-slate-800 bg-slate-900 shadow-xl">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left text-xs min-w-[650px]">
            <thead className="uppercase text-[10px] font-black border-b border-slate-800 bg-slate-950/70 text-slate-400 tracking-wider">
              <tr>
                <th className="py-4 px-5">Customer Profile</th>
                <th className="py-4 px-4">Contact Info</th>
                <th className="py-4 px-4">Orders & Spent</th>
                <th className="py-4 px-4">Joined Date</th>
                <th className="py-4 px-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-16 text-slate-400 space-y-2">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-400 mb-2" />
                    <p>কাস্টমার তালিকা লোড হচ্ছে...</p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-16 text-slate-500 space-y-2">
                    <Users className="w-10 h-10 mx-auto opacity-30 mb-2 text-slate-500" />
                    <p>এই আউটলেটে এখনও কোনো কাস্টমার নিবন্ধিত হয়নি।</p>
                  </td>
                </tr>
              ) : (
                filtered.map((cust) => {
                  const name =
                    cust.full_name ||
                    `${cust.first_name || ""} ${cust.last_name || ""}`.trim() ||
                    "Customer";
                  const initial = name.charAt(0).toUpperCase() || "C";

                  return (
                    <tr key={cust.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-4 px-5 flex items-center gap-3">
                        <div
                          style={{ backgroundColor: primaryColor }}
                          className="w-10 h-10 rounded-2xl text-white font-black flex items-center justify-center text-sm shadow-md shrink-0"
                        >
                          {initial}
                        </div>
                        <div className="min-w-0">
                          <span className="font-bold text-white block truncate">{name}</span>
                          <span className="text-[10px] text-slate-400 font-mono">ID: #{cust.id}</span>
                        </div>
                      </td>

                      <td className="py-4 px-4">
                        <div className="text-slate-200 truncate font-mono text-[11px]">{cust.email}</div>
                        <span className="text-[10px] text-slate-400 font-mono">{cust.phone || "No Phone"}</span>
                      </td>

                      <td className="py-4 px-4">
                        <div className="font-mono font-bold text-emerald-400 text-sm">
                          ৳{Number(cust.total_spent || 0).toLocaleString()}
                        </div>
                        <span className="text-[10px] text-slate-400">{cust.total_orders ?? 0} Orders</span>
                      </td>

                      <td className="py-4 px-4 text-slate-400 text-xs font-mono">
                        {cust.created_at ? new Date(cust.created_at).toLocaleDateString("en-GB") : "Recently"}
                      </td>

                      <td className="py-4 px-5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setViewCustomer(cust)}
                            className="p-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                            title="বিস্তারিত দেখুন"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => handleDeleteCustomer(cust.id, name)}
                            disabled={deleteId === cust.id}
                            className="p-2 rounded-xl bg-slate-950 border border-rose-500/20 text-rose-400 hover:bg-rose-500/20 transition-colors cursor-pointer disabled:opacity-50"
                            title="মুছে ফেলুন"
                          >
                            {deleteId === cust.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ৪. নতুন কাস্টমার যুক্ত করার মোডাল */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-md rounded-3xl bg-[#090d16] border border-slate-800 text-white shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <UserPlus className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black">Add Branch Customer</h3>
              </div>
              <button
                onClick={() => setIsAddOpen(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddCustomer} className="space-y-3.5">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">
                  Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Rahim Ahmed"
                  className="w-full px-3.5 py-2.5 rounded-xl text-xs font-bold bg-[#0d1424] border border-slate-800 text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">
                  Email Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="rahim@gmail.com"
                  className="w-full px-3.5 py-2.5 rounded-xl text-xs font-bold bg-[#0d1424] border border-slate-800 text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="01700000000"
                  className="w-full px-3.5 py-2.5 rounded-xl text-xs font-mono font-bold bg-[#0d1424] border border-slate-800 text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:bg-slate-800 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLoading}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-black text-white bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-600/30 cursor-pointer disabled:opacity-50"
                >
                  {addLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  <span>Save Customer</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ৫. ভিউ প্রোফাইল মোডাল */}
      {viewCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-md rounded-3xl bg-[#090d16] border border-slate-800 text-white shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-sm font-black">Customer Profile</h3>
              <button
                onClick={() => setViewCustomer(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-center gap-3">
              <div
                style={{ backgroundColor: primaryColor }}
                className="w-12 h-12 rounded-2xl text-white font-black text-xl flex items-center justify-center shadow-lg"
              >
                {(viewCustomer.full_name || viewCustomer.first_name || "C").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-black truncate">{viewCustomer.full_name}</h4>
                <p className="text-xs text-slate-400 font-mono">{viewCustomer.email}</p>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{viewCustomer.phone || "No Phone"}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800 text-center">
                <span className="text-[9px] uppercase font-bold text-slate-400 block">Total Orders</span>
                <span className="text-base font-black font-mono text-white mt-1 block">
                  {viewCustomer.total_orders ?? 0}
                </span>
              </div>
              <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800 text-center">
                <span className="text-[9px] uppercase font-bold text-slate-400 block">Total Spent</span>
                <span className="text-base font-black font-mono text-emerald-400 mt-1 block">
                  ৳{Number(viewCustomer.total_spent || 0).toLocaleString()}
                </span>
              </div>
            </div>

            <button
              onClick={() => setViewCustomer(null)}
              className="w-full py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}